import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import LoginForm from 'components/LoginLayout/LoginForm';
import { useMutation } from 'react-query';
import { isNotFinishSetup, JWT_TOKEN } from 'const/Const';
import Loader from 'components/Loader';
import { handleSuccess, decodeJwt } from 'utils';
import { addUser } from 'features/user/userSlice';
import { isAfter } from 'date-fns';
import AuthLayout from 'components/AuthLayout';
import SendEmail from 'components/LoginLayout/SendEmail';
import { LOGIN_MESSAGE } from 'const/Message';
import { Step } from 'types/ResetPassword';
import ForgotPassword from 'components/LoginLayout/ForgotPassword';
import { loginV2 } from 'services/authService';

type FormInputs = {
  email: string;
  password: string;
};

const Login = () => {
  useHandoffLogin();

  const [step, setStep] = useState<Step>('login');
  const [messageLogin, setMessageLogin] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();

  const token = localStorage.getItem(JWT_TOKEN);

  if (token) {
    const decode = decodeJwt(token);
    // check token is expired yet
    if (
      !Boolean(decode.exp) ||
      isAfter(new Date(decode.exp * 1000), Date.now())
    ) {
      history.push('/home');
    } else {
      localStorage.removeItem(JWT_TOKEN);
      history.push('/login');
    }
  }

  const handleLogin = (data: FormInputs) => {
    loginMutate(data, {
      onSuccess: (res: any) => {
        handleSuccess(LOGIN_MESSAGE.SUCCESS);
        localStorage.setItem(JWT_TOKEN, res.data.token);

        const decode = decodeJwt(res.data.token);
        dispatch(
          addUser({
            exp: decode.exp,
            iat: decode.iat,
            merchantId: decode.merchant_id,
            merchantSlug: decode.merchant_slug,
            roles: decode.roles,
            username: decode.username
          })
        );
        history.push('/home');
      },
      onError: () => {
        setMessageLogin('Le mot de passe saisi est incorrect');
      }
    });
  };

  const handleSendEmail = (status: Step) => {
    setMessageLogin('');
    setStep(status);
  };

  const { isLoading: isLoadingLogin, mutate: loginMutate } = useMutation(
    ({ email, password }: Required<FormInputs>) => loginV2({ email, password })
  );

  return (
    <AuthLayout>
      {isLoadingLogin && <Loader />}

      {step === 'login' && (
        <LoginForm
          title="Ravis de vous revoir !"
          errorLogin={messageLogin}
          formSubmit={handleLogin}
          setForgotPassword={() => setStep('sendEmail')}
        />
      )}

      {step === 'sendEmail' && (
        <SendEmail statusStep={handleSendEmail} title="Mot de passe oublié" />
      )}

      {step === 'forgotPassword' && (
        <ForgotPassword
          title="Réinitialisez votre mot de passe"
          statusStep={(status: Step) => setStep(status)}
          onBackSendEmail={() => setStep('sendEmail')}
        />
      )}
    </AuthLayout>
  );
};

export default Login;

function useHandoffLogin() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encoded = searchParams.get('token');

    if (!encoded) return;

    const cleanUrl = (to = '/login') => {
      history.replace({ pathname: to, search: '', hash: '' });
    };

    try {
      localStorage.removeItem(JWT_TOKEN);

      const jwt = atob(decodeURIComponent(encoded));
      const d = decodeJwt(jwt);

      if (d?.exp && !isAfter(new Date(d.exp * 1000), Date.now())) {
        throw new Error('Expired JWT');
      }

      localStorage.setItem(JWT_TOKEN, jwt);

      dispatch(
        addUser({
          exp: d.exp,
          iat: d.iat,
          merchantId: d.merchant_id,
          merchantSlug: d.merchant_slug,
          roles: d.roles,
          username: d.username
        })
      );

      cleanUrl('/home');
    } catch (err) {
      localStorage.removeItem(JWT_TOKEN);
      localStorage.removeItem(isNotFinishSetup);
      cleanUrl('/login');
    }
  }, [dispatch, history, location.search]);
}
